import { Container } from "@mui/material";

import {
  Badge,
  Button,
  CopyButton,
  Modal,
  Pagination,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";

import { GridRenderCellParams } from "@mui/x-data-grid";
import dayjs from "dayjs";
import DatagridDisplay from "../../Components/DatagridDisplay";

import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { useContext, useState } from "react";
import MegaLoader from "../../Components/MegaLoader";
import { Colors, HttpSuccessCodes } from "../../Lib/Data";
import { Endpoints } from "../../Lib/Endpoints";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  generateRandomString,
  getEllipsisFromString,
  getFinancialValueFromNumeric,
  getPaginationTotal,
} from "../../Lib/Methods";
import {
  DefaultAuthResponse,
  NonPaginatedResponse,
  PaginatedResponse,
} from "../../Lib/Responses";
import {
  PaginationProps,
  PromoterProps,
  UserProfileProps,
} from "../../Lib/Types";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";
import "./styles.scss";
import {
  IconKey,
  IconReload,
  IconUser,
  IconUserDown,
  IconUserUp,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { AppContext } from "../Dashboard";
const PromotersPage: React.FC = () => {
  const context = useContext(AppContext);
  const filterForm = useForm({
    initialValues: {
      service: "",
      userID: "",
    },
  });
  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    limit: 20,
  });
  const {
    data: promoters,
    reloadData: reloadPromoters,
    isLoading: isLoadingPromoters,
  } = usePerformRequest<PaginatedResponse<PromoterProps[]>>({
    method: "GET",
    route: Endpoints.GetPromotersList(pagination.page, pagination.limit),
  });

  const [
    isUserModalOpened,
    { open: OpenUserModal, close: ClosePromoterModal },
  ] = useDisclosure(false);

  const [currentPromoter, setCurrentPromoter] = useState<PromoterProps>();

  const [isUserPromotionToggling, setUserPromotionToggling] =
    useState<boolean>(false);

  const GetPromotionIcon = currentPromoter?.active ? IconUserDown : IconUserUp;

  const TogglePromotion = async () => {
    if (currentPromoter) {
      const active = !currentPromoter.active;
      setUserPromotionToggling(true);
      const r = await PerformRequest({
        method: "PATCH",
        route: Endpoints.GetUpdatePromoterStatus(currentPromoter.id as string),
        data: {
          active: active,
        },
        callback: () => {
          setUserPromotionToggling(false);
        },
      });
      setUserPromotionToggling(false);
      if (r) {
        if (HttpSuccessCodes.includes(r.status)) {
          DefaultSuccessNotification(
            `Promotion set to ${active ? "active" : "inactive"}`
          );
          ClosePromoterModal();
          reloadPromoters();
        } else {
          DefaultErrorNotification("An error occurred!");
        }
      } else {
        DefaultErrorNotification("An error occurred! Please try again");
      }
    }
  };
  const PromoteUser = async () => {
    if (currentPromoter) {
      setUserPromotionToggling(true);
      const r = await PerformRequest({
        method: "PATCH",
        route: Endpoints.GetConvertToPromoter(currentPromoter.id as string),

        callback: () => {
          setUserPromotionToggling(false);
        },
      });
      setUserPromotionToggling(false);
      if (r) {
        if (HttpSuccessCodes.includes(r.status)) {
          DefaultSuccessNotification(`User has been promoted!`);
          ClosePromoterModal();
          reloadPromoters();
        } else {
          DefaultErrorNotification("An error occurred!");
        }
      } else {
        DefaultErrorNotification("An error occurred! Please try again");
      }
    }
  };

  const [isLoggingInAsUser, setLoggingInAsUser] = useState(false);

  return (
    <Container>
      <div className="subscriptions-container">
        <div className="component-header">
          <div className="flex-row align-center justify-between width-100">
            <Text className="label">Promoters</Text>
            <Button
              leftSection={<IconReload size={16} />}
              onClick={reloadPromoters}
              loading={isLoadingPromoters}
            >
              Reload
            </Button>
          </div>
          <br />
          {currentPromoter && (
            <Modal
              opened={isUserModalOpened}
              onClose={() => {
                ClosePromoterModal();
                setCurrentPromoter(undefined);
              }}
              title="Promoter Details"
              centered
              size="lg"
            >
              <Table mb={20} variant="vertical" layout="fixed" withTableBorder>
                <Table.Thead>
                  <Table.Tr key={generateRandomString(12)}>
                    <Table.Th w={200}>Email Address</Table.Th>
                    <Table.Td>
                      <CopyButton value={currentPromoter.email}>
                        {({ copied, copy }) => (
                          <Tooltip
                            label={copied ? "Copied!" : "Copy to clipboard"}
                          >
                            <div
                              className="pointer flex-row align-center"
                              onClick={copy}
                              style={{
                                color: copied
                                  ? Colors.GreenPrimary
                                  : Colors.BluePrimary,
                                height: "100%",
                              }}
                            >
                              {currentPromoter.email}
                            </div>
                          </Tooltip>
                        )}
                      </CopyButton>
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr key={generateRandomString(12)}>
                    <Table.Th w={200}>Coupon</Table.Th>
                    <Table.Td>
                      <CopyButton value={currentPromoter.coupon}>
                        {({ copied, copy }) => (
                          <Tooltip
                            label={copied ? "Copied!" : "Copy to clipboard"}
                          >
                            <div
                              className="pointer flex-row align-center"
                              onClick={copy}
                              style={{
                                color: copied
                                  ? Colors.GreenPrimary
                                  : Colors.BluePrimary,
                                height: "100%",
                              }}
                            >
                              {currentPromoter.coupon}
                            </div>
                          </Tooltip>
                        )}
                      </CopyButton>
                    </Table.Td>
                  </Table.Tr>

                  <Table.Tr key={generateRandomString(12)}>
                    <Table.Th>Date Promoted</Table.Th>
                    <Table.Td>
                      {dayjs(currentPromoter.createdAt).format(
                        "DD MMM YYYY - hh:mmA"
                      )}
                    </Table.Td>
                  </Table.Tr>

                  <Table.Tr key={generateRandomString(12)}>
                    <Table.Th>Active</Table.Th>
                    <Table.Td
                      c={
                        currentPromoter.active
                          ? Colors.GreenSecondary
                          : Colors.BlackPrimary
                      }
                    >
                      {currentPromoter?.active ? "Yes" : "No"}
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr key={generateRandomString(12)}>
                    <Table.Th>Current Balance</Table.Th>
                    <Table.Td c={Colors.GreenSecondary} fw={500}>
                      ₦{getFinancialValueFromNumeric(currentPromoter.balance)}
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr key={generateRandomString(12)}>
                    <Table.Th>Lifetime Earnings</Table.Th>
                    <Table.Td c={Colors.GreenSecondary} fw={500}>
                      ₦
                      {getFinancialValueFromNumeric(
                        currentPromoter.totalEarnings
                      )}
                    </Table.Td>
                  </Table.Tr>
                </Table.Thead>
              </Table>
              <Link to={`/dashboard/promoter/${currentPromoter.userID}`}>
                <Button color={Colors.GreenPrimary}>View Details</Button>
              </Link>
            </Modal>
          )}
          {promoters && promoters.data && (
            <>
              <DatagridDisplay
                rows={promoters?.data as any}
                isLoading={isLoadingPromoters}
                onRowClick={(params) => {
                  OpenUserModal();
                  setCurrentPromoter(params.row);
                }}
                columns={[
                  {
                    field: "createdAt",
                    width: 150,

                    headerName: "Promotion Date",
                    renderCell: (
                      params: GridRenderCellParams<PromoterProps>
                    ) => {
                      return (
                        <div>
                          {dayjs(params.row.createdAt).format("DD MMM YYYY")}
                        </div>
                      );
                    },
                  },
                  {
                    field: "coupon",
                    width: 190,

                    headerName: "Coupon",
                    renderCell: (
                      params: GridRenderCellParams<PromoterProps>
                    ) => {
                      return (
                        <div
                          style={{
                            color: Colors.BluePrimary,
                            fontWeight: 500,
                            fontSize: 18,
                            textTransform: "uppercase",
                          }}
                        >
                          {params.row.coupon}
                        </div>
                      );
                    },
                  },

                  {
                    field: "balance",
                    width: 140,
                    headerName: "Balance",
                    renderCell: (
                      params: GridRenderCellParams<PromoterProps>
                    ) => {
                      return (
                        <div
                          style={{
                            color: Colors.GreenSecondary,
                            fontWeight: 600,
                          }}
                        >
                          ₦{getFinancialValueFromNumeric(params.row.balance)}
                        </div>
                      );
                    },
                  },
                  {
                    field: "totalEarnings",
                    width: 190,
                    headerName: "Lifetime Earnings",
                    renderCell: (
                      params: GridRenderCellParams<PromoterProps>
                    ) => {
                      return (
                        <div
                          style={{
                            color: Colors.GreenSecondary,
                            fontWeight: 600,
                          }}
                        >
                          ₦
                          {getFinancialValueFromNumeric(
                            params.row.totalEarnings
                          )}
                        </div>
                      );
                    },
                  },
                  {
                    field: "email",

                    minWidth: 250,
                    headerName: "Email Address",
                    renderCell: (
                      params: GridRenderCellParams<PromoterProps>
                    ) => {
                      return (
                        <div>
                          <CopyButton value={params.row.email}>
                            {({ copied, copy }) => (
                              <Tooltip
                                label={copied ? "Copied!" : "Copy to clipboard"}
                              >
                                <span
                                  className="pointer"
                                  onClick={copy}
                                  style={{
                                    color: copied
                                      ? Colors.GreenPrimary
                                      : Colors.BluePrimary,
                                  }}
                                >
                                  {getEllipsisFromString(params.row?.email, 50)}
                                </span>
                              </Tooltip>
                            )}
                          </CopyButton>
                        </div>
                      );
                    },
                  },

                  {
                    field: "active",
                    width: 100,

                    headerName: "Active",
                    renderCell: (
                      params: GridRenderCellParams<PromoterProps>
                    ) => {
                      return (
                        <div>
                          <Badge
                            variant={params.row.active ? "filled" : "outline"}
                            color={
                              params.row.active
                                ? Colors.GreenPrimary
                                : Colors.BluePrimary
                            }
                          >
                            {params.row.active ? "Yes" : "No"}
                          </Badge>
                        </div>
                      );
                    },
                  },
                ]}
              />
              <div className="flex-row align-center justify-center width-100">
                <Pagination
                  value={pagination.page}
                  onChange={(value) => {
                    setPagination({ ...pagination, page: value });
                  }}
                  my={40}
                  disabled={promoters?.data?.length === 0 || isLoadingPromoters}
                  total={getPaginationTotal(
                    promoters?.totalRecords,
                    pagination.limit
                  )}
                />
              </div>
            </>
          )}
          {isLoadingPromoters && <MegaLoader />}
        </div>
      </div>
    </Container>
  );
};

export default PromotersPage;
